import { z } from 'zod';
import type { Via } from '../enums';
import type { HistoryActor, HistoryChange } from './audit';
import { idSchema, type StatusTone } from './common';

/**
 * Notatki klienta i oś czasu klienta (Fala 10, DECISIONS 2026-10-07 [S] „Wszystko z codziennej pracy da się zlecić
 * agentowi przez MCP” — „notatki na kliencie widoczne na osi czasu (zakładka Historia)”).
 *
 * Usługi (moduł `history`, `packages/core/src/modules/history`, router tRPC `history.*`): `addClientNote`,
 * `listClientNotes`, `updateClientNote`, `pinClientNote`, `archiveClientNote`, `getClientNoteVersions` oraz
 * `getClientTimeline` (zakładka „Historia” w profilu klienta; z tej samej usługi korzysta narzędzie MCP
 * `get_client_history`, a z `addClientNote` — narzędzie MCP `add_client_note` i narzędzie Copilota `add_client_note`).
 *
 * Notatka = lekka tabela `client_notes` (+ wersje `client_note_versions`), nie Wiedza: uprawnienia modułu Klienci
 * i zakresu klienta (Wiedza ma własny moduł), notatka nie trafia do listy Wiedzy, a oś czasu czyta ją wprost.
 * Źródło notatki wynika z kanału aktora: UI = ręcznie, AI = Copilot, MCP = agent przez MCP, worker = automatyzacja.
 */

// ── Notatki ─────────────────────────────────────────────────────────────────

export const CLIENT_NOTE_SOURCES = ['manual', 'copilot', 'mcp', 'automation'] as const;
export type ClientNoteSource = (typeof CLIENT_NOTE_SOURCES)[number];

/** Kanał aktora → źródło notatki (ui = ręcznie, ai = Copilot, mcp = agent przez MCP, worker / system = automatyzacja). */
export function clientNoteSourceOf(via: Via): ClientNoteSource {
  if (via === 'ai') return 'copilot';
  if (via === 'mcp') return 'mcp';
  if (via === 'worker' || via === 'system') return 'automation';
  return 'manual';
}

export const CLIENT_NOTE_MAX_CHARS = 20_000;

export const clientNoteBodySchema = z
  .string()
  .trim()
  .min(1, { message: 'Wpisz treść notatki' })
  .max(CLIENT_NOTE_MAX_CHARS, { message: `Najwyżej ${CLIENT_NOTE_MAX_CHARS.toLocaleString('pl-PL')} znaków` });

export const addClientNoteInput = z
  .object({
    clientId: idSchema,
    bodyMd: clientNoteBodySchema,
    /** opcjonalne powiązania (ten sam klient) */
    projectId: idSchema.nullable().optional(),
    taskId: idSchema.nullable().optional(),
    meetingId: idSchema.nullable().optional(),
    /** przypnij od razu (ważna notatka) */
    pinned: z.boolean().optional(),
  })
  .strict();
export type AddClientNoteInput = z.input<typeof addClientNoteInput>;

export const listClientNotesInput = z
  .object({
    clientId: idSchema,
    /** tylko przypięte (Przegląd: „Ostatnie notatki” pokazuje przypięte nad ostatnimi) */
    pinnedOnly: z.boolean().optional(),
    includeArchived: z.boolean().optional(),
    /** szukaj w treści (bez wielkości liter i polskich znaków) */
    q: z.string().trim().max(100).optional(),
    limit: z.number().int().min(1).max(100).default(20),
    /** kursor: wartość `nextCursor` poprzedniej strony */
    cursor: z.string().max(200).optional(),
  })
  .strict();
export type ListClientNotesInput = z.input<typeof listClientNotesInput>;

export const updateClientNoteInput = z
  .object({
    id: idSchema,
    /** wersja wiersza (edycja współbieżna: inna = CONFLICT) */
    version: z.number().int().positive(),
    bodyMd: clientNoteBodySchema.optional(),
    projectId: idSchema.nullable().optional(),
    taskId: idSchema.nullable().optional(),
    meetingId: idSchema.nullable().optional(),
  })
  .strict()
  .refine(
    (v) => v.bodyMd !== undefined || v.projectId !== undefined || v.taskId !== undefined || v.meetingId !== undefined,
    { message: 'Brak zmian' },
  );
export type UpdateClientNoteInput = z.input<typeof updateClientNoteInput>;

export const pinClientNoteInput = z.object({ id: idSchema, pinned: z.boolean() }).strict();
export type PinClientNoteInput = z.input<typeof pinClientNoteInput>;

export const archiveClientNoteInput = z.object({ id: idSchema, archived: z.boolean().default(true) }).strict();
export type ArchiveClientNoteInput = z.input<typeof archiveClientNoteInput>;

export const clientNoteIdInput = z.object({ id: idSchema }).strict();

export type ClientNotePerson = HistoryActor;

export interface ClientNoteDto {
  id: string;
  clientId: string;
  bodyMd: string;
  source: ClientNoteSource;
  /** autor (null = automatyzacja bez osoby) */
  author: ClientNotePerson | null;
  createdAt: Date;
  /** ostatnia zmiana treści (null = bez edycji) */
  editedAt: Date | null;
  editedBy: ClientNotePerson | null;
  /** numer wersji treści (1 = bez edycji); historia: `getClientNoteVersions` */
  versionNo: number;
  /** wersja wiersza do `updateClientNote` */
  version: number;
  pinned: boolean;
  pinnedAt: Date | null;
  archivedAt: Date | null;
  /** powiązania widoczne dla osoby (rekord poza zakresem albo bez modułu = null) */
  project: { id: string; name: string } | null;
  task: { id: string; number: number; title: string } | null;
  meeting: { id: string; title: string; startsAt: Date } | null;
  /** czy osoba może zmienić treść / przypiąć / zarchiwizować (UI; decyduje usługa) */
  canEdit: boolean;
  canPin: boolean;
}

export interface ClientNoteList {
  items: ClientNoteDto[];
  nextCursor: string | null;
}

export interface ClientNoteVersionDto {
  versionNo: number;
  bodyMd: string;
  source: ClientNoteSource;
  editedBy: ClientNotePerson | null;
  createdAt: Date;
}

/**
 * Podgląd notatki z narzędzia Copilota `add_client_note` w rozmowie z danymi klienta (maile, notatki, spotkania —
 * treść mogła pochodzić z polecenia ukrytego w danych): nic nie jest zapisane, dopóki osoba nie potwierdzi w UI
 * (`history.notes.add`).
 */
export interface ChatClientNotePreview {
  clientId: string;
  clientName: string;
  bodyMd: string;
  projectId: string | null;
  taskId: string | null;
  meetingId: string | null;
  pinned: boolean;
}

export const CLIENT_NOTE_MESSAGES = {
  notFound: 'Nie znaleziono notatki',
  editForbidden: 'Treść notatki zmienia jej autor albo właściciel, administrator lub manager',
  archived: 'Notatka jest w archiwum — przywróć ją, żeby edytować',
  wrongClient: 'Powiązany rekord należy do innego klienta',
  clientArchived: 'Klient jest w archiwum',
  needsConfirmation:
    'Rozmowa zawiera dane klienta (np. treść maila), więc notatka czeka na Twoje potwierdzenie — sprawdź treść.',
} as const;

// ── Oś czasu klienta (zakładka „Historia”) ──────────────────────────────────

/**
 * Rodzaje wpisów osi czasu: notatki, zmiany danych (audyt klienta i kontaktów), maile, spotkania, zadania
 * (utworzone / zamknięte), wpisy czasu, akceptacje, przebiegi agentów i pozostałe zdarzenia (alarmy, odnowienia,
 * faktury — bez zdarzeń, które są już innym rodzajem: wiadomości, akceptacje, przebiegi).
 */
export const CLIENT_HISTORY_KINDS = [
  'note',
  'change',
  'mail',
  'meeting',
  'task',
  'time',
  'approval',
  'agent_run',
  'event',
] as const;
export type ClientHistoryKind = (typeof CLIENT_HISTORY_KINDS)[number];

export const clientHistoryInput = z
  .object({
    clientId: idSchema,
    /** filtr rodzajów (brak = wszystkie dostępne osobie) */
    kinds: z.array(z.enum(CLIENT_HISTORY_KINDS)).min(1).max(CLIENT_HISTORY_KINDS.length).optional(),
    limit: z.number().int().min(1).max(100).default(30),
    /** kursor: `nextCursor` poprzedniej strony */
    cursor: z.string().max(300).optional(),
    /** dopisywane przez `useInfiniteQuery` (tRPC v11); lista zawsze od najnowszych */
    direction: z.enum(['forward', 'backward']).optional(),
  })
  .strict();
export type ClientHistoryInput = z.input<typeof clientHistoryInput>;

export interface ClientHistoryEntry {
  /** unikalny klucz wpisu `<rodzaj>:<id>` (np. `task_done:<id>`) */
  key: string;
  kind: ClientHistoryKind;
  /** szczegółowy rodzaj, np. `note`, `client.update`, `mail_in`, `mail_out`, `task_created`, `task_done` */
  type: string;
  at: Date;
  /** zdanie po polsku („Anna Kowalczyk napisała: Sekcja FAQ”, „Zamknięto zadanie #12 Stopka”) */
  title: string;
  /** szczegół (fragment treści, opis wpisu czasu, status) */
  detail: string | null;
  /** osoba (autor zmiany, notatki, wpisu) */
  actor: HistoryActor | null;
  /** kto zamiast osoby albo obok niej: „Copilot”, „Claude (MCP)”, „Automatyzacja”, nadawca maila */
  actorLabel: string | null;
  via: Via | null;
  /** rekord do otwarcia */
  link: { type: string; id: string } | null;
  status: { label: string; tone: StatusTone } | null;
  /** rodzaj `note`: pełna notatka (edycja, przypięcie, wersje) */
  note?: ClientNoteDto;
  /** rodzaj `change`: zmienione pola */
  changes?: HistoryChange[];
  /** wpis z symulacji (zdarzenie testowe, adapter fake) */
  simulated?: boolean;
  /**
   * Treść napisała osoba klienta w portalu (zgłoszenie, wiadomość zgłoszenia): dla AI i MCP dane niezaufane
   * (łata 17.10.2026; MCP `from_client` + `untrusted_note`).
   */
  fromClient?: true;
}

export interface ClientHistoryPage {
  items: ClientHistoryEntry[];
  nextCursor: string | null;
  /** rodzaje dostępne osobie (bez modułu = brak rodzaju; UI pokazuje tylko te filtry) */
  kinds: ClientHistoryKind[];
}

export const CLIENT_HISTORY_MESSAGES = {
  badCursor: 'Nieprawidłowy kursor listy',
} as const;
